<template>
	<o-card
		class="task-modal-edit-card"
		:class="{
			'task-modal-edit-card--full' : setup.isOpen,
			'task-modal-edit-card--zero' : selectedTaskType && !setup.isOpen,
			'task-modal-edit-card--new': setup.isNew,
			'task-modal-edit-card--select': setup.isTemplate,
		}">
		<span
			v-if="setup.isOpen"
			class="task-modal-edit-card__back"
			@click.prevent.stop="$emit('close')"/>

		<div
			v-if="setup.isOpen"
			class="task-modal-edit-card__card-content">
			<p v-if="setup.isNew">{{ task.isNew ? `Nouvelle tâche` : `Modification` }}</p>
			<p v-else-if="setup.isTemplate">Tâche récurrente</p>
			<div
				class="row row--grid-xs task-modal-edit-card__form"
				:class="{ 'task-modal-edit-card__form--anim': setup.animForm }">
				<template v-if="setup.isNew">
					<div class="col-xs-12">
						<div class="flex g-xs">
							<v-dropdown class="task-modal-edit-card__icon-select-trigger">
								<div class="orion-input__input">
									<o-icon :font-icon="task.icon"/>
								</div>
								<template #popper="{ hide }">
									<div
										v-close-popper
										class="flex g-xs">
										<o-icon
											v-for="iconOption in setup.iconOptions"
											:key="`${iconOption + Math.random()}`"
											v-close-popper
											class="icon-to-select"
											:font-icon="iconOption"
											size="sm"
											@click="task.icon = iconOption; hide()"/>
									</div>
								</template>
							</v-dropdown>

							<o-input
								ref="taskInput"
								v-model="task.title"
								label="Renseignez le titre de la tâche"/>
						</div>
					</div>

					<div class="col-xs-12">
						<o-textarea
							v-model="task.description"
							label="Renseignez la description de la tâche"/>
					</div>
				</template>
				<div
					v-else-if="setup.isTemplate"
					class="col-xs-12">
					<o-select
						ref="taskSelect"
						v-model="task.title"
						class="task-modal-edit-card__select-task"
						label="Sélectionnez une tâche enregistrée"
						track-key="id"
						value-key="title"
						display-key="title"
						:options="setup.taskOptions"
						clearable
						@select="setup.handleTaskTemplateSelect($event)">
						<template #value="{ item }">
							<span v-if="item">
								<o-icon :font-icon="item.icon"/>
								{{ item.title }}
							</span>
						</template>
						<template #option="{ item }">
							<div class="flex ai-c g-sm">
								<o-icon
									class="text--bigger"
									:font-icon="`${item.icon}`"/>
								<div>
									<strong>{{ item.title }}</strong>
									<div>
										{{
											item.description.length > 50
												? item.description.slice(0, 50) + '...'
												: item.description
										}}
									</div>
								</div>
							</div>
						</template>
					</o-select>
				</div>

				<div class="col-xs-12">
					<o-datepicker
						ref="taskDueDate"
						v-model="task.startDate"
						suffix-font-icon="icon-interface-calendar-mark"
						label="Date prévue"
						:min-date="new Date()"
						time/>
				</div>

				<div class="col-xs-12">
					<o-select
						v-model="task.duration"
						label="Durée de la tâche"
						track-key="id"
						value-key="value"
						display-key="label"
						:options="setup.durationOptions"/>
				</div>

				<div class="col-xs-12">
					<shared-select-assignee
						v-model="task.assignedUserId"
						:options="assignedUserOptions"/>
				</div>
			</div>
		</div>

		<div
			v-if="!setup.isOpen && setup.isNew"
			class="task-modal-edit-card__add-content">
			<o-icon font-icon="icon-interface-add-circle"/>
			<p>
				Créer une<br>
				nouvelle tâche
			</p>
		</div>
		<div
			v-else-if="!setup.isOpen && setup.isTemplate"
			class="task-modal-edit-card__add-content">
			<o-icon font-icon="icon-interface-file-folder"/>
			<p>
				Sélectionner une<br>
				tâche récurrente
			</p>
		</div>
	</o-card>
</template>

<script setup lang="ts">
import SharedSelectAssignee from '../shared/SharedSelectAssignee.vue';
import TaskModalEditCardSetupService from '@/setup/task/TaskModalEditCardSetupService';
defineEmits<{(e:'close'): void}>();
const props = defineProps(TaskModalEditCardSetupService.props);
const setup = new TaskModalEditCardSetupService(props);
</script>

<style scoped lang="less">
@import '@/styles/mixins.less';

.task-modal-edit-card {
  flex: 1;
  height: 210px;
  transition: all .4s;
  border: 1px dashed;

  :deep(.orion-input) {
    margin: unset;
  }

	&__select-task {
		.orion-icon {
			margin-right: 10px;
		}
	}

  &__form {
    > * {
      transition: opacity 0.3s, transform 0.3s;
      will-change: opacity, transform;
      opacity: 0;
      transform: translate3d(0, -2rem, 0);
    }

    &--anim {
      > * {
        opacity: 1;
        transform: translate3d(0, 0, 0);

        each(range(9), {
          &:nth-child(@{value}) {
            transition-delay: calc(0.10s + @value * 0.07s);
          }
        })
      }
    }
  }

  &__icon-select-trigger {
    cursor: pointer;

    > .orion-input__input {
      .flex-align();
      aspect-ratio: 1;
      padding: unset;
    }

    + .orion-input {
      flex: 1;
    }
  }

	/* &__top-input {
		display: flex;
		gap: 10px;

		.orion-input {
			flex: 1;
		}
	} */

	&__add-content {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 100%;
		width: 100%;
		cursor: pointer;

		.orion-icon {
			font-size: 34px;
			margin-bottom: 10px;
		}

		p {
      width: 8rem;
			font-size: 12px;
			font-weight: 600;
			text-transform: uppercase;
			text-align: center;
		}
	}

	&__card-content {
		p {
			font-weight: 600;
			font-size: 14px;
			margin-bottom: 15px;
			margin-top: -5px;
		}
	}

	&__back {
		.close(20px);
		position: absolute;
		top: 15px;
		right: 15px;
	}

	.orion-alert {
		margin-bottom:20px;
	}

  &--full {
    height: unset;
    border-style: solid;
    box-shadow: none !important;
    transform: unset !important;
  }

  &--zero {
    flex: 0;
    opacity: 0;
    overflow: hidden;
    margin-right: -20px;
  }

  &--new {
    border-color: var(--info);
    .task-modal-edit-card__add-content {
      color: var(--info);
    }
  }

  &--select {
    border-color: var(--brand);
    .task-modal-edit-card__add-content {
      color: var(--brand);
    }
  }
}

.icon-to-select {
	font-size: 1.25rem;
	padding: 0.75rem;
	border-radius: 0.25rem;

	&:hover {
		background: var(--grey-lighter);
	}
}
</style>
